import { C } from '../../lib/tokens.js';
import { useLookups } from '../../lib/LookupsContext.jsx';
import { useSelfLookup } from '../../lib/selfLookups.js';
import { useState, useRef } from 'react';
import { useClickOutside } from '../../lib/useClickOutside.js';

// Prefers the Context value (instant, no extra request, when this component
// happens to be running in the same bundle as <LookupsProvider>) and falls
// back to a self-fetch otherwise - see selfLookups.js for why the fallback is
// needed at all.
export function useResilientLookup(ctxList, path) {
  const list = ctxList ?? [];
  const self = useSelfLookup(path, list.length === 0);
  return list.length ? list : self;
}

export function StatusChip({ 
  id, 
  label, 
  size = 'md', 
  filled = false, 
  onChange, 
  disabled = false, 
  readOnly = false
}) {
  const { statuses: ctxStatuses } = useLookups() ?? {};
  const statuses = useResilientLookup(ctxStatuses, '/lookups/statuses');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const s = statuses.find(x => x.id === id) || { color: C.silver, bg: C.mercury, fg: C.fg, name: id };
  const fs = size === 'sm' ? 10 : 11;
  const padY = size === 'sm' ? 2 : 3;

  useClickOutside(dropdownRef, !readOnly, () => setIsOpen(false));

  const isInteractive = !readOnly && !disabled;

  const baseStyle = filled ? {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: `${padY}px ${isInteractive ? '6px' : '8px'} ${padY}px 8px`, borderRadius: 999,
    background: s.bg, color: s.fg, fontSize: fs, fontWeight: 500,
    border: 'none', cursor: isInteractive ? 'pointer' : 'default',
    fontFamily: 'inherit', outline: 'none'
  } : {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: `${padY}px ${isInteractive ? '6px' : '8px'} ${padY}px 8px`, borderRadius: 3,
    background: 'transparent', color: s.fg, fontSize: fs, fontWeight: 500,
    border: `1px solid ${s.color}40`, cursor: isInteractive ? 'pointer' : 'default',
    fontFamily: 'inherit', outline: 'none'
  };

  if (readOnly) {
    return (
      <span style={baseStyle}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flex: 'none' }} />
        <span>{label || s.name}</span>
      </span>
    );
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        style={baseStyle}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flex: 'none' }} />
        <span>{label || s.name}</span>
        {!disabled && (
          <span className="material-icons" style={{ fontSize: fs + 3, color: s.color, opacity: 0.8, marginLeft: 1 }}>
            arrow_drop_down
          </span>
        )}
      </button>

      {isOpen && statuses.length > 0 && (
        <ChipDropdownMenu
          align="left"
          minWidth={140}
          maxWidth={null}
          // The terminal status is a system-only final state (e.g. auto-set on archival) —
          // not a state an agent should be able to pick manually from this dropdown.
          options={statuses.filter(status => !status.terminal).map(status => ({ id: status.id, name: status.name, color: status.color }))}
          isSelected={(optId) => optId === id}
          onSelect={(optId) => {
            if (optId !== id) onChange?.(optId);
            setIsOpen(false);
          }}
        />
      )}
    </div>
  );
}

export function findLookup(list, value) {
  if (value == null) return undefined;
  const needle = String(value).toLowerCase();
  return (
    list.find(x => x.id === value) ??
    list.find(x => String(x.id).toLowerCase() === needle) ??
    list.find(x => x.name?.toLowerCase() === needle)
  );
}

function ChipDropdownButton({ color, bg, icon, label, size, isInteractive, onClick }) {
  const fs = size === 'sm' ? 10 : 11;
  const padY = size === 'sm' ? 2 : 3;
  return (
    <button
      type="button"
      disabled={!isInteractive}
      onClick={onClick}
      title={label}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%',
        padding: `${padY}px ${size === 'sm' ? 6 : 8}px`, borderRadius: 3,
        background: bg, color, fontSize: fs, fontWeight: 500, border: 'none',
        cursor: isInteractive ? 'pointer' : 'default', fontFamily: 'inherit', outline: 'none',
      }}
    >
      <span className="material-icons" style={{ fontSize: fs + 2, flex: 'none' }}>{icon}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: '1 1 auto' }}>
        {label}
      </span>
      {isInteractive && (
        <span className="material-icons" style={{ fontSize: fs + 3, opacity: 0.8, flex: 'none' }}>
          arrow_drop_down
        </span>
      )}
    </button>
  );
}

function ChipDropdownMenu({ options, isSelected, onSelect, align = 'right', minWidth = 160, maxWidth = 240 }) {
  return (
    <div style={{
      position: 'absolute',
      top: '100%',
      [align === 'left' ? 'left' : 'right']: 0,
      marginTop: 4,
      background: '#ffffff',
      border: `1px solid ${C.mercury}`,
      borderRadius: 4,
      boxShadow: '0 3px 10px rgba(0,0,0,0.12)',
      zIndex: 1000,
      minWidth,
      maxWidth,
      padding: '4px 0',
      maxHeight: 220,
      overflowY: 'auto'
    }}>
      {options.map(opt => (
        <div
          key={opt.id}
          onClick={() => onSelect(opt.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            fontSize: 12,
            color: C.fg,
            cursor: 'pointer',
            background: isSelected(opt.id) ? C.rowActive : 'transparent',
            fontWeight: isSelected(opt.id) ? 500 : 400,
          }}
          onMouseEnter={(e) => { if (!isSelected(opt.id)) e.currentTarget.style.background = C.platinum; }}
          onMouseLeave={(e) => { if (!isSelected(opt.id)) e.currentTarget.style.background = 'transparent'; }}
        >
          {opt.icon
            ? <span className="material-icons" style={{ fontSize: 14, color: opt.color, flex: 'none' }}>{opt.icon}</span>
            : <span style={{ width: 6, height: 6, borderRadius: '50%', background: opt.color, flex: 'none' }} />}
          <span style={{ flex: 1 }}>{opt.name}</span>
          {isSelected(opt.id) && (
            <span className="material-icons" style={{ fontSize: 12, color: C.liquid }}>check</span>
          )}
        </div>
      ))}
    </div>
  );
}

const DOC_STATUS_STYLES = {
  CO: { label: 'Completed',           color: '#3F7A00', bg: '#EEF8DD' }, // green
  CL: { label: 'Closed',              color: '#3F7A00', bg: '#EEF8DD' }, // green
  RE: { label: 'Reversed',            color: C.iron,    bg: C.mercury }, // gray
  DR: { label: 'Draft',               color: C.iron,    bg: C.mercury }, // gray
  IP: { label: 'In Progress',         color: '#A86600', bg: '#FCEFD9' }, // yellow
  WC: { label: 'Waiting Confirmation', color: '#A86600', bg: '#FCEFD9' }, // yellow
  WP: { label: 'Waiting Payment',     color: '#A86600', bg: '#FCEFD9' }, // yellow
  WU: { label: 'Waiting Update',      color: '#A86600', bg: '#FCEFD9' }, // yellow
  VO: { label: 'Voided',              color: '#9E0617', bg: '#FBE4E7' }, // red
  IN: { label: 'Invalid',             color: '#9E0617', bg: '#FBE4E7' }, // red
  NA: { label: 'Not Approved',        color: '#9E0617', bg: '#FBE4E7' }, // red
};

// Chip for raw ADempiere DocStatus codes (order/invoice status) — separate from
// StatusChip, which is driven by the request/ticket status lookup list.
export function DocStatusChip({ id, label, size = 'md' }) {
  const key = String(id ?? '').toUpperCase();
  const s = DOC_STATUS_STYLES[key] || { label: label || id || '—', color: C.iron, bg: C.mercury };
  const fs = size === 'sm' ? 10 : 11;
  const padY = size === 'sm' ? 2 : 3;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: `${padY}px 8px`, borderRadius: 999,
      background: s.bg, color: s.color, fontSize: fs, fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flex: 'none' }} />
      <span>{label || s.label}</span>
    </span>
  );
}

// minWidth:0 + maxWidth:100% on the outer span (instead of the old unconditional
// whiteSpace:nowrap, which forced full intrinsic width even inside a shrinking flex
// row) let this actually shrink to fit - e.g. a Kanban card's footer row, where a
// long cluster/request-cluster name used to push the assignee avatar out past the
// card's edge instead of truncating. The icon keeps flexShrink:0 so it's never the
// thing that gets squeezed; only the text ellipsizes.
export function Chip({ children, color = C.iron, bg = C.platinum, icon, size = 'md', onClick, dim }) {
  const fs = size === 'sm' ? 10 : 11;
  return (
    <span onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: size === 'sm' ? '2px 6px' : '3px 8px',
      borderRadius: 3, background: bg, color, fontSize: fs, fontWeight: 500,
      cursor: onClick ? 'pointer' : 'default', opacity: dim ? 0.7 : 1,
      minWidth: 0, maxWidth: '100%',
    }}>
      {icon ? <span className="material-icons" style={{ fontSize: fs + 2, flexShrink: 0 }}>{icon}</span> : null}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{children}</span>
    </span>
  );
}

const PRIORITY_FALLBACK = {
  low:      { color: C.iron,    bg: C.mercury,   icon: 'expand_more' },
  normal:   { color: '#1A5273', bg: C.rowActive, icon: 'remove' },
  high:     { color: '#A86600', bg: '#FCEFD9', icon: 'expand_less' },
  critical: { color: '#9E0617', bg: '#FBE4E7', icon: 'priority_high' },
};

export function PriorityChip({ id, size = 'md', onChange, disabled = false, readOnly = false }) {
  const { priorities: ctxPriorities } = useLookups() ?? {};
  const priorities = useResilientLookup(ctxPriorities, '/lookups/priorities');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const p = findLookup(priorities, id);
  const fb = PRIORITY_FALLBACK[p?.id ?? id] || PRIORITY_FALLBACK.normal;
  const color = p?.color || fb.color;
  const bg    = p?.color ? `${p.color}22` : fb.bg;
  const label = p?.name  || id || 'Normal';
  const icon  = fb.icon;

  useClickOutside(dropdownRef, !readOnly, () => setIsOpen(false));

  if (readOnly) {
    return <Chip color={color} bg={bg} icon={icon} size={size}>{label}</Chip>;
  }

  const isInteractive = !disabled;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
      <ChipDropdownButton
        color={color} bg={bg} icon={icon} label={label} size={size}
        isInteractive={isInteractive}
        onClick={isInteractive ? () => setIsOpen(o => !o) : undefined}
      />

      {isOpen && priorities.length > 0 && (
        <ChipDropdownMenu
          options={priorities.map(pr => ({ id: pr.id, name: pr.name, color: pr.color || C.iron }))}
          isSelected={(optId) => optId === p?.id}
          onSelect={(optId) => {
            if (optId !== p?.id) onChange?.(optId);
            setIsOpen(false);
          }}
        />
      )}
    </div>
  );
}

const ICON_STYLE = {
  bug_report:   { color: '#9E0617', bg: '#FBE4E7' },
  help:         { color: '#1A5273', bg: C.rowActive },
  help_outline: { color: '#1A5273', bg: C.rowActive },
  add_circle:   { color: '#3F7A00', bg: '#EEF8DD' },
  lightbulb:    { color: '#3F7A00', bg: '#EEF8DD' },
  warning:      { color: '#A86600', bg: '#FCEFD9' },
  storage:      { color: C.deepsea, bg: '#E1E5F1' },
  table_view:   { color: C.deepsea, bg: '#E1E5F1' },
  // Request type icons set per type in the ERP (R_RequestType.AFSP_PortalIcon)
  receipt_long:         { color: '#5A3E85', bg: '#EEE6F7' },
  engineering:          { color: '#0E7C86', bg: '#DFF3F3' },
  volunteer_activism:   { color: '#7A2E8E', bg: '#F4E6F7' },
  admin_panel_settings: { color: C.iron,    bg: C.platinum },
};

const MODULE_STYLE = { color: '#5A3E85', bg: '#EEE6F7' };
const MODULE_UNASSIGNED_STYLE = { color: C.silver, bg: C.platinum };

const CLUSTER_STYLE = { color: '#0E7C86', bg: '#DFF3F3' };
const CLUSTER_UNASSIGNED_STYLE = { color: C.silver, bg: C.platinum };

export function ModuleChip({ id, size = 'md', onChange, disabled = false, readOnly = false }) {
  const { modules: ctxModules } = useLookups() ?? {};
  const modules = useResilientLookup(ctxModules, '/lookups/modules');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentId = id || '';
  const mod = findLookup(modules, id);
  const label = mod?.name ?? (currentId ? id : 'Unassigned');
  const style = currentId ? MODULE_STYLE : MODULE_UNASSIGNED_STYLE;

  useClickOutside(dropdownRef, !readOnly, () => setIsOpen(false));

  if (readOnly) {
    return <Chip color={style.color} bg={style.bg} icon="apps" size={size}>{label}</Chip>;
  }

  const isInteractive = !disabled;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
      <ChipDropdownButton
        color={style.color} bg={style.bg} icon="apps" label={label} size={size}
        isInteractive={isInteractive}
        onClick={isInteractive ? () => setIsOpen(o => !o) : undefined}
      />

      {isOpen && (
        <ChipDropdownMenu
          options={[
            { id: '', name: 'Unassigned', icon: 'apps', color: MODULE_UNASSIGNED_STYLE.color },
            ...modules.map(m => ({ id: m.id, name: m.name, icon: 'apps', color: MODULE_STYLE.color })),
          ]}
          isSelected={(optId) => optId === currentId}
          onSelect={(optId) => {
            if (optId !== currentId) onChange?.(optId);
            setIsOpen(false);
          }}
        />
      )}
    </div>
  );
}

export function ClusterChip({ name, size = 'md' }) {
  const style = name ? CLUSTER_STYLE : CLUSTER_UNASSIGNED_STYLE;
  return <Chip color={style.color} bg={style.bg} icon="hub" size={size}>{name || 'Unassigned'}</Chip>;
}

export function TypeChip({ id, size = 'md', onChange, disabled = false, readOnly = false }) {
  const { requestTypes: ctxRequestTypes } = useLookups() ?? {};
  const requestTypes = useResilientLookup(ctxRequestTypes, '/lookups/request-types');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const type = findLookup(requestTypes, id);
  const icon = type?.icon || 'help_outline';
  const style = ICON_STYLE[icon] || { color: '#1A5273', bg: '#E1F2FA' };
  const label = type?.name ?? id;

  useClickOutside(dropdownRef, !readOnly, () => setIsOpen(false));

  if (readOnly) {
    return <Chip color={style.color} bg={style.bg} icon={icon} size={size}>{label}</Chip>;
  }

  const isInteractive = !disabled;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
      <ChipDropdownButton
        color={style.color} bg={style.bg} icon={icon} label={label} size={size}
        isInteractive={isInteractive}
        onClick={isInteractive ? () => setIsOpen(o => !o) : undefined}
      />

      {isOpen && requestTypes.length > 0 && (
        <ChipDropdownMenu
          options={requestTypes.map(t => ({ id: t.id, name: t.name, icon: t.icon || 'help_outline', color: (ICON_STYLE[t.icon] || ICON_STYLE.help_outline).color }))}
          isSelected={(optId) => optId === type?.id}
          onSelect={(optId) => {
            if (optId !== type?.id) onChange?.(optId);
            setIsOpen(false);
          }}
        />
      )}
    </div>
  );
}
